import { useEffect, useRef } from 'react';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';
import { EntryPath } from '@/Config/app/router/entry.path';
import { trackAndNavigate } from '@/Config/libs/analytics';
import { ReCaptchaCheckbox } from '@/Domain/components/Auth/ReCaptchaCheckbox';
import { Alert, AlertDescription } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { FieldError } from '@/Domain/components/Shared/Field/Field';
import { PasswordInput } from '@/Domain/components/Shared/Input/PasswordInput';
import LockLineIcon from '@/Domain/components/icons/LockLineIcon';
import { useResetPassword } from '@/Domain/hooks/Auth/useResetPassword.hook';

export const ResetPasswordPage = () => {
  const navigate = useNavigate();

  const { t } = useTranslation(['reset-password', 'reset_password_success']);
  const { track } = useAnalytics();

  const {
    onVerify,
    isPending,
    isSuccess,
    onSubmit,
    handleSubmit,
    errors,
    register,
    handleV2Focus,
    shouldFocusV2,
    useV2,
    control,
    targetEmail,
    isLinkInvalid,
  } = useResetPassword();
  const hasInvalidLink = !!errors.token || isLinkInvalid;
  const invalidLinkRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasInvalidLink) {
      invalidLinkRef.current?.focus();
    }
  }, [hasInvalidLink]);

  const handleSuccess = () => {
    trackAndNavigate(
      'rest_password_back_success_click',
      { event_category: 'reset_password_form', event_label: 'User reset password' },
      () => navigate(EntryPath.login, { viewTransition: true }),
    );
  };

  const handleBackTrack = () =>
    track('rest_password_back', {
      event_category: 'reset_password_form',
      event_label: 'User back to login',
    });

  if (isSuccess) {
    return (
      <div className="animate-in fade-in duration-300">
        {/* Success State */}
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success-muted">
            <svg
              className="h-8 w-8 text-success"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="mb-2 text-2xl font-semibold text-foreground">
            {t('reset_password_success:title')}
          </h1>
        </div>
        <p className="mb-6 text-center text-muted-foreground">
          {t('reset_password_success:description')}
        </p>
        <Button variant="dark" size="lg" className="w-full" onClick={handleSuccess}>
          {t('reset_password_success:link')}
        </Button>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in duration-300">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="mb-2 text-2xl font-semibold text-foreground">{t('reset-password:title')}</h1>
        <p className="text-sm text-muted-foreground">
          {targetEmail
            ? t('reset-password:subtitle_for_account', { email: targetEmail })
            : t('reset-password:subtitle')}
        </p>
      </div>

      <div className="grid gap-5">
        {/* Token Error */}
        {hasInvalidLink && (
          <Alert
            ref={invalidLinkRef}
            variant="destructive"
            className="animate-in slide-in-from-top-2 duration-200 fade-in"
            data-testid="reset-password-token-error"
            tabIndex={-1}
          >
            <AlertDescription>{t('reset-password:validation.invalid_token')}</AlertDescription>
          </Alert>
        )}

        {/* Form */}
        {!hasInvalidLink && (
          <form
            id="reset-password-form"
            className="flex flex-col gap-5"
            onSubmit={handleSubmit(onSubmit)}
          >
            <GoogleReCaptcha onVerify={onVerify} />

            {/* New Password Field */}
            <div className="space-y-1.5">
              <label htmlFor="password1" className="text-sm font-medium text-foreground">
                {t('reset-password:form.password')}
              </label>
              <PasswordInput
                id="password1"
                size="lg"
                data-testid="reset-password-new"
                placeholder={t('reset-password:form.password_placeholder')}
                {...register('new_password1')}
                error={!!errors.new_password1}
                autoFocus
                autoComplete="new-password"
                startIcon={<LockLineIcon className="h-4 w-4 text-foreground-subtle" />}
              />
              {errors.new_password1 && (
                <p className="text-sm text-red-600 flex items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-200">
                  {errors.new_password1.message}
                </p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-1.5">
              <label htmlFor="password2" className="text-sm font-medium text-foreground">
                {t('reset-password:form.confirm_password')}
              </label>
              <PasswordInput
                id="password2"
                size="lg"
                data-testid="reset-password-confirm"
                placeholder={t('reset-password:form.password_placeholder')}
                {...register('new_password2')}
                error={!!errors.new_password2}
                autoComplete="new-password"
                startIcon={<LockLineIcon className="h-4 w-4 text-foreground-subtle" />}
              />
              {errors.new_password2 && (
                <p className="text-sm text-red-600 flex items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-200">
                  {errors.new_password2.message}
                </p>
              )}
            </div>

            {errors.root && (
              <Alert
                variant="destructive"
                data-testid="reset-password-error"
                className="animate-in fade-in duration-200"
              >
                <AlertDescription>{errors.root.message}</AlertDescription>
              </Alert>
            )}

            {/* reCAPTCHA v2 Fallback */}
            {useV2 && (
              <Controller
                name="recaptcha_response_v2"
                control={control}
                render={({ field: { onChange, value }, fieldState: { invalid } }) => (
                  <ReCaptchaCheckbox
                    onChange={onChange}
                    onFocusHandled={handleV2Focus}
                    value={value}
                    isInvalid={invalid}
                    shouldFocus={shouldFocusV2}
                  />
                )}
              />
            )}
            {errors.recaptcha_response_v2 && (
              <FieldError className="text-center" data-testid="reset-password-captcha-error">
                {t('validation:required')}
              </FieldError>
            )}

            {/* Submit Button */}
            <Button
              variant="dark"
              size="lg"
              type="submit"
              loading={isPending}
              className="w-full mt-1"
              data-testid="reset-password-submit"
            >
              {t('reset-password:button.submit')}
            </Button>
          </form>
        )}

        {/* Back to Login Link */}
        <div className="border-t border-border pt-4 text-center">
          <p className="text-sm text-muted-foreground">
            {t('reset-password:back_to')}{' '}
            <Link
              className="font-medium text-foreground transition-colors hover-underline"
              to="/"
              onClick={handleBackTrack}
            >
              {t('reset-password:link.login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
